import { useEffect, useRef, useState } from 'react';

//Primereact components
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toolbar } from 'primereact/toolbar';

import { JobPortalTab } from './JobPortalTab';
import { getAllDetails, postAllData } from '../../service/ProductService';
import { ADD_DEPARTMENT, DEPARTMENT_LIST, UPDATE_DEPARTMENT } from '../../config/ApiConstant';
import { SkeletonbodyTemplate, Skeletonitems } from '../../appconfig/Settings';
import { DepartmentListColumns } from '../../appconfig/DatatableSetting';
import { Dialog } from 'primereact/dialog';
import { AddUpdateDepartmentValidate } from '../../config/Validate';

export const DepartmentList = () => {
    document.title = 'Department List | GSPC';

    //Set Toast/ Filter Properties
    const toast = useRef<any>(null);

    //Loading/Page Loading
    const [pageLoad, setPageLoad] = useState<boolean>(true);
    const [departments, setDepartments] = useState<any>([]);
    const [name, setName] = useState<any>('');
    const [addUpdateLoader, setAddUpdateLoader] = useState<boolean>(false);
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [editId, setEditId] = useState<any>(null);
    const [errors, setErrors] = useState<any>({});
    const [globalFilter, setGlobalFilter] = useState<any>(null);

    // useEffect
    useEffect(() => {
        getDepartmentsList();
    }, []);

    // global search
    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                    
                </span>
            </>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <>
                <Button label="Add Department" icon="pi pi-plus" className="p-button" onClick={() => openAddModal()} />
            </>
        );
    };

    // Get departments list
    const getDepartmentsList = async () => {
        try {
            setPageLoad(false);
            const response = await getAllDetails(DEPARTMENT_LIST);
            if (response) {
                const DataList: any = response;
                if (DataList.length === 0) {
                    setDepartments([]);
                } else {
                    setDepartments(DataList);
                }
                setPageLoad(true);
            } else {
                setPageLoad(false);
                setDepartments([]);
            }
        } catch (error: any) {
            setPageLoad(false);
            setDepartments([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Sorry Could Not Load Data'
            });
        }
    };

    // Open add modal
    const openAddModal = () => {
        setAddUpdateModal(true);
    };

    // Close add modal
    const closeAddUpdateModal = () => {
        setName('');
        setEditId(null);
        setAddUpdateLoader(false);
        setAddUpdateModal(false);
    };

    // Open edit modal
    const openEditModal = (rowData: any) => {
        setName(rowData.dname);
        setEditId(rowData.id);
        setAddUpdateModal(true);
    };

    // Template for action
    const actionsTemplate = (rowData: any, rowIndex: any) => {
        return (
            <>
                <div className="flex">
                    <Button icon="pi pi-pencil" className="p-button-rounded" onClick={() => openEditModal(rowData)} />
                </div>
            </>
        );
    };

    // On submit add update department
    const onSubmitAddUpdateDepartment = async () => {
        try {
            const { errors, isError } = AddUpdateDepartmentValidate(name);
            setErrors(errors);

            //Check Error if no errors then call API
            if (!isError) {
                setAddUpdateLoader(true);

                let formData: any = new FormData();
                let apiSlug = ADD_DEPARTMENT;
                if (editId !== null) {
                    apiSlug = UPDATE_DEPARTMENT;
                    formData.append('id', editId);
                }
                formData.append('dname', name);

                //apicall
                const response = await postAllData(apiSlug, formData);
                if (response && !window.cn(response)) {
                    setName('');
                    setEditId(null);
                    setAddUpdateLoader(false);
                    toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
                    setTimeout(() => {
                        setAddUpdateModal(false);
                        getDepartmentsList();
                    }, 1000);
                }
            }
        } catch (error: any) {
            if (error.response) {
                //Spinner False and Set Toast

                setAddUpdateLoader(false);
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something Went Wrong' });
            }
        }
    };

    return (
        <>
            <JobPortalTab  mainTabLabel="Department List" />
            <Toast ref={toast} />
            <div className="grid crud-demo mt-3">
                <div className="col-12">
                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                        {/* Datatable */}
                        {pageLoad ? (
                            <>
                                <DataTable value={departments} globalFilter={globalFilter} showGridlines stripedRows className="datatable-responsive p-datatable-sm" emptyMessage="No Departments Found">
                                    {DepartmentListColumns.map((col, i) => {
                                        if (col.field === 'action') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={actionsTemplate} />;
                                        } else {
                                            return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                        }
                                    })}
                                </DataTable>
                            </>
                        ) : (
                            <>
                                {/* Skeleton Data table */}
                                <DataTable value={Skeletonitems}>
                                    {DepartmentListColumns.map((col, i) => (
                                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                    ))}
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Update Dialog */}
            <Dialog
                visible={addUpdateModal}
                className="p-fluid"
                style={{ width: '70vh' }}
                header={editId !== null ? 'Edit Department' : 'Add Department'}
                modal
                draggable={false}
                resizable={false}
                footer={
                    <>
                        <Button label="Cancel" className="p-button-secondary" icon="pi pi-times" onClick={closeAddUpdateModal} />
                        <Button label="Submit" icon="pi pi-check" className="p-button-primary" onClick={() => onSubmitAddUpdateDepartment()} loading={addUpdateLoader} />
                    </>
                }
                onHide={closeAddUpdateModal}
            >
                <div className="formgrid grid">
                    <div className="field col-12">
                        <label htmlFor="dname">
                            Department Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText value={name} name="dname" autoComplete="off" placeholder="Enter Department Name" onChange={(e) => setName(e.target.value)} className={errors['dname'] && 'p-invalid'} />
                        <small className="p-invalid-txt">{errors['dname']}</small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
